import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  TrophyIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { winnersAPI } from '../services/api';
import {
  LoadingSpinner,
  EmptyState,
  StatCard,
  Badge,
  Modal,
  FilterButton,
  DataTable,
  Pagination,
} from '../components/common';
import { formatCurrency, formatDate, formatPhone } from '../utils/formatters';

export default function WinnersPage() {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const [isMarkPaidModalOpen, setIsMarkPaidModalOpen] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [payoutFilter, setPayoutFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: winnersData, isLoading } = useQuery({
    queryKey: ['winners', payoutFilter, currentPage, pageSize],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (payoutFilter !== 'all') params.payout_status = payoutFilter;
      const response = await winnersAPI.getAll(params);
      return response.data;
    },
  });

  const winners = winnersData?.data || [];
  const pagination = winnersData?.pagination || { total: 0, pages: 1 };

  const markPaidMutation = useMutation({
    mutationFn: ({ id, data }) => winnersAPI.markPaid(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['winners']);
      toast.success('Winner marked as paid successfully!');
      setIsMarkPaidModalOpen(false);
      setSelectedWinner(null);
      reset();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to mark as paid');
    },
  });

  const handleMarkPaid = (winner) => {
    setSelectedWinner(winner);
    setIsMarkPaidModalOpen(true);
  };

  const onSubmit = (data) => {
    markPaidMutation.mutate({
      id: selectedWinner.id,
      data: {
        payout_reference: data.payout_reference,
        payout_notes: data.payout_notes,
      },
    });
  };

  const columns = [
    {
      key: 'id',
      label: 'Winner ID',
      render: (value) => `#${value}`,
    },
    {
      key: 'user_phone',
      label: 'User',
      render: (value, row) => formatPhone(value || row.user_id),
    },
    {
      key: 'draw_id',
      label: 'Draw',
      render: (value) => `Draw #${value}`,
    },
    {
      key: 'prize_amount_cents',
      label: 'Prize Amount',
      render: (value) => formatCurrency(value || 0),
    },
    {
      key: 'payout_status',
      label: 'Payout Status',
      render: (value) => (
        <Badge variant={
          value === 'paid' ? 'success' :
          value === 'processing' ? 'warning' :
          'default'
        }>
          {value?.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Win Date',
      render: (value) => formatDate(value),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, winner) => (
        winner.payout_status !== 'paid' ? (
          <button
            onClick={() => handleMarkPaid(winner)}
            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
          >
            Mark as Paid
          </button>
        ) : (
          <span className="text-green-600 text-sm flex items-center gap-1">
            <CheckCircleIcon className="h-4 w-4" />
            Paid
          </span>
        )
      ),
    },
  ];

  const stats = {
    total: winners?.length || 0,
    paid: winners?.filter(w => w.payout_status === 'paid').length || 0,
    processing: winners?.filter(w => w.payout_status === 'processing').length || 0,
    notPaid: winners?.filter(w => w.payout_status === 'not_paid').length || 0,
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading winners..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Winners</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage lottery winners and payout status
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Winners"
          value={stats.total}
          icon={TrophyIcon}
          color="primary"
        />
        <StatCard
          title="Paid"
          value={stats.paid}
          icon={CheckCircleIcon}
          color="green"
        />
        <StatCard
          title="Processing"
          value={stats.processing}
          icon={ClockIcon}
          color="yellow"
        />
        <StatCard
          title="Not Paid"
          value={stats.notPaid}
          icon={TrophyIcon}
          color="gray"
        />
      </div>

      <div className="flex gap-2 flex-wrap">
        <FilterButton
          active={payoutFilter === 'all'}
          onClick={() => setPayoutFilter('all')}
        >
          All Winners
        </FilterButton>
        <FilterButton
          active={payoutFilter === 'not_paid'}
          onClick={() => setPayoutFilter('not_paid')}
        >
          Not Paid
        </FilterButton>
        <FilterButton
          active={payoutFilter === 'processing'}
          onClick={() => setPayoutFilter('processing')}
        >
          Processing
        </FilterButton>
        <FilterButton
          active={payoutFilter === 'paid'}
          onClick={() => setPayoutFilter('paid')}
        >
          Paid
        </FilterButton>
      </div>

      <div className="card">
        {winners && winners.length > 0 ? (
          <>
            <DataTable columns={columns} data={winners} />
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              itemsPerPage={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
            />
          </>
        ) : (
          <EmptyState
            icon={TrophyIcon}
            title="No winners found"
            description={payoutFilter !== 'all' 
              ? `No winners with ${payoutFilter.replace('_', ' ')} status` 
              : "Winners will appear here after draws are completed"
            }
          />
        )}
      </div>

      <Modal
        isOpen={isMarkPaidModalOpen}
        onClose={() => {
          setIsMarkPaidModalOpen(false);
          setSelectedWinner(null);
          reset();
        }}
        title="Mark Winner as Paid"
        size="md"
      >
        {selectedWinner && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Winner ID: <span className="font-semibold">#{selectedWinner.id}</span>
            </p>
            <p className="text-sm text-gray-600">
              Prize Amount: <span className="font-semibold">{formatCurrency(selectedWinner.prize_amount_cents || 0)}</span>
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payout Reference *
            </label>
            <input
              {...register('payout_reference', { required: 'Payout reference is required' })}
              type="text"
              className="input-field"
              placeholder="TXN123456789"
            />
            {errors.payout_reference && (
              <p className="mt-1 text-sm text-red-600">{errors.payout_reference.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              {...register('payout_notes')}
              rows={3}
              className="input-field"
              placeholder="Additional notes about the payout..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsMarkPaidModalOpen(false);
                setSelectedWinner(null);
                reset();
              }}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={markPaidMutation.isLoading}
              className="flex-1 btn-primary"
            >
              {markPaidMutation.isLoading ? 'Processing...' : 'Mark as Paid'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
